import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Users, Zap, Star, Check, Shield, Play, Pause, Volume2, Share2 } from "lucide-react";
import { format } from "date-fns";
import { useAudio } from "../contexts/AudioContext";
import { ShareButton } from "./SocialShare";

// Currency formatting for EU/UK
const formatPrice = (price, country) => {
  const isUK = country?.toLowerCase().includes('uk') || country?.toLowerCase().includes('united kingdom');
  const currency = isUK ? '£' : '€';
  const convertedPrice = isUK ? price * 0.79 : price * 0.92;
  return `${currency}${Math.round(convertedPrice)}`;
};

// RA-STANDARD SUB-GENRES for XTrip Approved badge
// Only these specific sub-genres qualify - NO generic 'Electronic'
const RA_STANDARD_SUBGENRES = [
  'techno', 'deep house', 'melodic', 'warehouse', 'minimal', 'underground',
  'house', 'acid', 'industrial', 'dark techno', 'hard techno', 'dub techno',
  'tech house', 'progressive house', 'afro house', 'detroit techno'
];

// Keywords that indicate RA-standard events
const RA_KEYWORDS = [
  'techno', 'deep house', 'melodic', 'warehouse', 'minimal', 'underground',
  'berghain', 'tresor', 'fabric', 'printworks', 'dc10', 'amnesia', 'awakenings',
  'time warp', 'movement', 'dekmantel', 'sonar', 'boiler room'
];

// Check if event qualifies for XTrip Approved badge
// Uses RA-STANDARD sub-genre filtering (NOT generic 'Electronic')
const isXTripApproved = (event) => {
  const genre = (event.genre || '').toLowerCase();
  const eventName = (event.name || '').toLowerCase();
  const venueName = (event.venue_name || '').toLowerCase();
  
  // Check if genre matches RA-standard sub-genres
  const isRAStandardGenre = RA_STANDARD_SUBGENRES.some(g => genre.includes(g));
  
  // Check if event name or venue contains RA keywords
  const hasRAKeyword = RA_KEYWORDS.some(k => 
    eventName.includes(k) || venueName.includes(k) || genre.includes(k)
  );
  
  // All Resident Advisor events are auto-approved
  if (event.source === 'resident_advisor') {
    return true;
  }
  
  // Ticketmaster events: ONLY if they match RA-standard sub-genres (bypass generic 'Electronic')
  if (event.source === 'ticketmaster') {
    // Must have specific sub-genre OR RA keyword - NOT just 'Electronic'
    return isRAStandardGenre || hasRAKeyword;
  }
  
  // Dice events with RA-standard genre
  if (event.source === 'dice' || event.source === 'songkick') {
    return isRAStandardGenre || hasRAKeyword;
  }
  
  // XTrip curated events (no source or source is 'xtrip')
  if (!event.source || event.source === 'xtrip') {
    return isRAStandardGenre || hasRAKeyword;
  }
  
  return false;
};

// Scene score badge logic
const getSceneBadge = (score, source) => {
  // RA is auto-approved as authoritative
  if (source === 'resident_advisor') {
    return { label: "RA CERTIFIED", color: "text-primary", bg: "bg-primary/20", icon: Star };
  }
  if (score >= 10) return { label: "LEGEND", color: "text-yellow-400", bg: "bg-yellow-400/20", icon: Zap };
  if (score >= 7) return { label: "CERTIFIED", color: "text-purple-400", bg: "bg-purple-400/20", icon: Star };
  if (score >= 4) return { label: "UNDERGROUND", color: "text-green-400", bg: "bg-green-400/20", icon: Check };
  if (score >= 3) return { label: "APPROVED", color: "text-blue-400", bg: "bg-blue-400/20", icon: Check };
  return null;
};

// Source badge
const SOURCE_LABELS = {
  resident_advisor: "RA",
  dice: "DICE",
  ticketmaster: "TM",
  songkick: "SK",
  xceed: "XC",
  xtrip: "XT"
};

/**
 * XCEED AUDIO PREVIEW - Uses Global Audio Controller
 * 
 * THE 'ONE TRACK' RULE:
 * - Clicking play on Card B automatically stops Card A
 * - UI syncs globally via useAudio context
 * - Sound Profile tracking integrated
 */
const XceedAudioPreview = ({ trackUrl, eventName, eventId, genre, artist }) => {
  const { playTrack, isTrackPlaying } = useAudio();
  const isPlaying = isTrackPlaying(eventId);
  const [isLoading, setIsLoading] = useState(false);
  
  const togglePlay = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!trackUrl) {
      console.log('[Audio] No track URL available');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Use global audio controller - handles One Track Rule automatically
      await playTrack(trackUrl, eventId, {
        event_id: eventId,
        event_name: eventName,
        genre: genre || 'Electronic',
        track_url: trackUrl,
        artist: artist
      });
    } catch (err) {
      console.log('[Audio] Playback error:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Don't render if no track URL
  if (!trackUrl) return null;
  
  return (
    <button
      onClick={togglePlay}
      disabled={isLoading}
      className="flex items-center gap-2 px-3 py-2 rounded-full transition-all hover:scale-105 active:scale-95"
      style={{
        background: isPlaying ? 'rgba(204, 255, 0, 0.3)' : 'rgba(0, 0, 0, 0.8)',
        border: isPlaying ? '2px solid rgba(204, 255, 0, 0.8)' : '2px solid rgba(204, 255, 0, 0.4)',
        boxShadow: isPlaying ? '0 0 16px rgba(204, 255, 0, 0.6)' : '0 0 8px rgba(204, 255, 0, 0.3)',
        cursor: isLoading ? 'wait' : 'pointer'
      }}
      data-testid="xceed-audio-toggle"
      title={isPlaying ? 'Pause preview' : 'Play preview'}
    >
      {isLoading ? (
        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      ) : isPlaying ? (
        <>
          <Pause className="w-4 h-4 text-primary" />
          <Volume2 className="w-4 h-4 text-primary animate-pulse" />
        </>
      ) : (
        <>
          <Play className="w-4 h-4 text-primary fill-primary" />
          <span className="text-xs text-primary font-mono uppercase font-bold">Preview</span>
        </>
      )}
    </button>
  );
};

// Mobile Feed Card - Optimized for thumb zone & scannability
const EventCardFeed = ({ event }) => {
  const [imgError, setImgError] = useState(false);
  
  // STRICT FILTER: Hide events with bad/missing names only
  if (!event?.name || 
      event.name === 'MEL' || 
      event.name === 'Undefined' ||
      event.name.length < 3) {
    return null;
  }
  
  const startDate = new Date(event.start_at);
  const displayPrice = formatPrice(event.ticket_price, event.venue_country);
  const approved = isXTripApproved(event);
  // NO FALLBACK RULE: Only show audio if track_url actually exists
  const hasAudioPreview = event.audio === true && event.track_url;
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Get original artwork - prioritize high-res sources, proxy external images
  const getImageUrl = () => {
    // Priority: large_artwork_url > event_flyer_url > flyer_url > image_url
    const originalUrl = event.large_artwork_url || 
                       event.event_flyer_url || 
                       event.flyer_url || 
                       event.artwork_url ||
                       event.image_url;
    
    if (!originalUrl) return null;
    
    // Proxy external images that block hotlinking (Xceed, RA, Dice, Sudbeat)
    const needsProxy = [
      'ra.co', 'i.ra.co', 
      'xceed.me', 'cdn.xceed', 'xceed.com',
      'dice.fm', 
      'sudbeat', 
      'residentadvisor'
    ].some(domain => originalUrl.toLowerCase().includes(domain));
    
    if (needsProxy) {
      return `${API_URL}/api/proxy-image?url=${encodeURIComponent(originalUrl)}`;
    }
    return originalUrl;
  };
  
  const imageUrl = getImageUrl();
  
  return (
    <div className="block glass rounded-xl overflow-hidden" data-testid={`event-feed-${event.event_id}`}>
      <Link 
        to={`/events/${event.event_id}`}
        className="block p-3 active:scale-[0.98] transition-transform"
      >
        <div className="flex gap-3">
          {/* Original Artwork - Compact */}
          <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 relative bg-muted">
            {imageUrl && !imgError ? (
              <img 
                src={imageUrl}
                alt={event.name}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={() => setImgError(true)}
              />
            ) : (
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, 
                    hsl(${(event.name?.charCodeAt(0) || 0) % 360}, 60%, 15%) 0%, 
                    hsl(${((event.name?.charCodeAt(0) || 0) + 120) % 360}, 50%, 8%) 100%)`
                }}
              >
                <span className="font-mono text-xs font-bold text-white/40 px-1 text-center line-clamp-2">
                  {event.name?.split(':')[0]?.slice(0, 12) || 'EVENT'}
                </span>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            {/* XTrip Approved mini badge on mobile feed */}
            {approved && (
              <span 
                className="absolute top-1 right-1 w-4 h-4 rounded-full border border-cyan-400/60 flex items-center justify-center"
                style={{ 
                  background: 'rgba(0, 0, 0, 0.7)',
                  boxShadow: '0 0 6px rgba(34, 211, 238, 0.5)'
                }}
              >
                <Shield className="w-2.5 h-2.5 text-cyan-400" />
              </span>
            )}
            {/* Xceed Audio mini icon on mobile feed */}
            {hasAudioPreview && !approved && (
              <span 
                className="absolute top-1 right-1 w-4 h-4 rounded-full border border-primary/60 flex items-center justify-center"
                style={{ 
                  background: 'rgba(0, 0, 0, 0.7)',
                  boxShadow: '0 0 6px rgba(204, 255, 0, 0.5)'
                }}
              >
                <Volume2 className="w-2.5 h-2.5 text-primary" />
              </span>
            )}
          </div>
          
          {/* High-Contrast Scannable Content */}
          <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
            {/* Event Name - High contrast monospace */}
            <h3 className="font-mono font-bold text-sm text-foreground line-clamp-1">
              {event.name}
            </h3>
            
            {/* Location & Date */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3 text-secondary flex-shrink-0" />
              <span className="truncate">{event.venue_city}</span>
            </div>
            
            {/* Price & Date Row - High Scannability */}
            <div className="flex items-center justify-between">
              <span className="font-mono text-lg font-black text-primary">
                {displayPrice}
              </span>
              <span className="font-mono text-xs text-accent bg-accent/10 px-2 py-0.5 rounded">
                {format(startDate, 'MMM dd')}
              </span>
            </div>
          </div>
        </div>
      </Link>
      
      {/* XCEED AUDIO PREVIEW - Embedded player for Mobile */}
      {hasAudioPreview && (
        <div 
          className="mx-3 mb-3 p-2 rounded-lg border border-primary/30 flex items-center justify-between"
          style={{
            background: 'linear-gradient(135deg, rgba(204, 255, 0, 0.08) 0%, rgba(0, 0, 0, 0.4) 100%)',
          }}
        >
          <div className="flex items-center gap-2">
            <Volume2 className="w-3 h-3 text-primary" />
            <span className="text-[10px] font-mono text-primary uppercase">Hear the Vibe</span>
          </div>
          <XceedAudioPreview 
            trackUrl={event.track_url} 
            eventName={event.name}
            eventId={event.event_id}
            genre={event.genre}
            artist={event.artists?.[0]}
          />
        </div>
      )}
    </div>
  );
};

// Default Grid Card
const EventCard = ({ event, index = 0, variant = "grid" }) => {
  const [imgError, setImgError] = useState(false);
  
  // STRICT FILTER: Hide events with bad/missing names only
  if (!event?.name || 
      event.name === 'MEL' || 
      event.name === 'Undefined' ||
      event.name.length < 3) {
    return null;
  }
  
  const startDate = new Date(event.start_at);
  const displayPrice = formatPrice(event.ticket_price, event.venue_country);
  const sceneBadge = getSceneBadge(event.scene_score, event.source);
  const sourceLabel = SOURCE_LABELS[event.source] || "XT";
  
  // NO FALLBACK RULE: Only show audio if track_url actually exists
  const hasAudioPreview = event.audio === true && event.track_url;
  
  // Get original artwork - use proxy for external images to bypass hotlinking
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const getImageUrl = () => {
    // Priority: large_artwork_url > event_flyer_url > flyer_url > image_url
    const originalUrl = event.large_artwork_url || 
                       event.event_flyer_url || 
                       event.flyer_url || 
                       event.artwork_url ||
                       event.image_url;
    
    // No image URL - return null (will trigger fallback)
    if (!originalUrl) return null;
    
    // Proxy external images that block hotlinking (Xceed, RA, Dice, Sudbeat)
    const needsProxy = [
      'ra.co', 'i.ra.co', 
      'xceed.me', 'cdn.xceed', 'xceed.com',
      'dice.fm', 
      'sudbeat', 
      'residentadvisor'
    ].some(domain => originalUrl.toLowerCase().includes(domain));
    
    if (needsProxy) {
      return `${API_URL}/api/proxy-image?url=${encodeURIComponent(originalUrl)}`;
    }
    
    // Direct URL for allowed sources (Unsplash, Pexels, Ticketmaster)
    return originalUrl;
  };
  
  const imageUrl = getImageUrl();
  
  // Use feed variant on mobile
  if (variant === "feed") {
    return <EventCardFeed event={event} />;
  }
  
  return (
    <Link 
      to={`/events/${event.event_id}`}
      className={`block card-hover animate-fade-in-up opacity-0 stagger-${(index % 5) + 1}`}
      data-testid={`event-card-${event.event_id}`}
      style={{ animationFillMode: 'forwards' }}
    >
      <div className="bg-card border border-border overflow-hidden group">
        {/* Original Artwork - No generic fallbacks unless truly missing */}
        <div className="relative aspect-[16/9] overflow-hidden bg-muted">
          {imageUrl && !imgError ? (
            <img 
              src={imageUrl}
              alt={event.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            // Branded placeholder - shows event name initial, not genre
            <div 
              className="w-full h-full flex items-center justify-center relative"
              style={{
                background: `linear-gradient(135deg, 
                  hsl(${(event.name?.charCodeAt(0) || 0) % 360}, 70%, 12%) 0%, 
                  hsl(${((event.name?.charCodeAt(0) || 0) + 120) % 360}, 60%, 6%) 100%)`
              }}
            >
              <div 
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.1) 0%, transparent 50%)'
                }}
              />
              <div className="text-center z-10 px-4">
                <div className="font-heading text-2xl font-bold text-white/30 line-clamp-2">
                  {event.name?.split(':')[0] || event.genre || 'EVENT'}
                </div>
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent z-0" />
          
          {/* Source Badge - Small, subtle */}
          <div className="absolute top-4 left-4">
            <span className="px-2 py-1 text-[10px] font-mono bg-black/60 text-muted-foreground rounded backdrop-blur-sm">
              {sourceLabel}
            </span>
          </div>
          
          {/* Price Tag & Share */}
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <span className="price-tag font-mono">
              {displayPrice}
            </span>
            <div onClick={(e) => e.preventDefault()}>
              <ShareButton event={event} variant="icon" className="!p-1.5" />
            </div>
          </div>
          
          {/* XTrip Approved Badge - Bottom Left */}
          {isXTripApproved(event) && (
            <div className="absolute bottom-4 left-4">
              <span 
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider rounded-full border-2 border-cyan-400/60"
                style={{ 
                  fontFamily: "'Space Mono', monospace",
                  background: 'rgba(0, 0, 0, 0.7)',
                  boxShadow: '0 0 12px rgba(34, 211, 238, 0.4), inset 0 0 8px rgba(34, 211, 238, 0.1)',
                  textShadow: '0 0 8px rgba(34, 211, 238, 0.8)'
                }}
              >
                <Shield className="w-3 h-3 text-cyan-400" />
                <span className="text-cyan-400 font-bold">XTrip Approved</span>
              </span>
            </div>
          )}
          
          {/* Scene Badge - Bottom Left (if present and not showing XTrip Approved) */}
          {sceneBadge && !isXTripApproved(event) && (
            <div className="absolute bottom-4 left-4">
              <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider rounded ${sceneBadge.bg} ${sceneBadge.color}`}>
                <sceneBadge.icon className="w-3 h-3" />
                {sceneBadge.label}
              </span>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-3">
          <h3 className="font-heading font-bold text-lg text-foreground group-hover:text-primary transition-colors line-clamp-1">
            {event.name}
          </h3>
          
          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4 text-secondary" />
              <span>{event.venue_city}, {event.venue_country}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4 text-secondary" />
              <span className="font-mono text-xs">{format(startDate, 'MMM dd, yyyy')}</span>
            </div>
          </div>
          
          {/* Artists */}
          {event.artists && event.artists.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Users className="w-3 h-3" />
              <span className="line-clamp-1">{event.artists.slice(0, 3).join(' • ')}</span>
            </div>
          )}
          
          {/* XCEED AUDIO PREVIEW - Embedded player for events with audio */}
          {hasAudioPreview && (
            <div 
              className="mt-3 p-3 rounded-lg border border-primary/30"
              style={{
                background: 'linear-gradient(135deg, rgba(204, 255, 0, 0.05) 0%, rgba(0, 0, 0, 0.3) 100%)',
                boxShadow: '0 0 20px rgba(204, 255, 0, 0.1)'
              }}
              onClick={(e) => e.preventDefault()}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-primary" />
                  <span className="text-xs font-mono text-primary uppercase tracking-wider">Hear the Vibe</span>
                </div>
                <XceedAudioPreview 
                  trackUrl={event.track_url} 
                  eventName={event.name}
                  eventId={event.event_id}
                  genre={event.genre}
                  artist={event.artists?.[0]}
                />
              </div>
              {event.artists?.[0] && (
                <div className="mt-2 text-xs text-muted-foreground font-mono truncate">
                  {event.artists[0]}
                </div>
              )}
            </div>
          )}
          
          {/* Availability + Let's Go */}
          <div className="pt-3 border-t border-border space-y-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground uppercase tracking-wider">Availability</span>
              <span className="font-mono text-primary">
                {event.tickets_available > 100 ? 'AVAILABLE' : 'LIMITED'}
              </span>
            </div>
            
            {/* Blood Red INTERNAL Let's Go Button - styled div, parent Link handles navigation */}
            <div
              className="block w-full py-2.5 text-center text-sm font-bold uppercase tracking-wider rounded-lg transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
                color: '#FFFFFF',
                boxShadow: '0 4px 14px rgba(220, 38, 38, 0.4)',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}
              data-testid="lets-go-btn"
            >
              Let's Go
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default EventCard;
